/**
 * A shared, in-memory brake on retries.
 *
 * `Sunny16BaseService` bounds what a *single* request does — idempotent methods only, two attempts,
 * seconds apart. Nothing bounded what the client does in aggregate, and on 2026-09-16 that mattered:
 * Sunny16's catalog resolution collapsed under load while the storefront kept replaying the reads
 * that were failing. Rationale:
 * `docs/features/2026.09.18-cart-state-reconciliation/implementation-plans/2026.09.18-001-cart-state-reconciliation.md`
 *
 * **This suppresses retries only; it never blocks a first attempt.** The worst it can do to a
 * customer is surface the failure they were going to see anyway, sooner — the same deal `POST`
 * already gets. A breaker that blocked first attempts would bound more load, but the client cannot
 * tell "the server is down" from "this phone just lost signal", and getting that wrong takes a
 * working app away from someone whose connection merely hiccuped.
 *
 * State is module-level so every service instance shares one view of how the API is doing, and
 * deliberately in memory: a reload is a new page and a fresh chance. Persisting a tripped brake
 * would punish exactly the customer who reloaded to get out of trouble, and reloads were a large
 * part of the 09-16 traffic.
 */
import { appErrorService } from '@/services/AppErrorService'

/**
 * Consecutive retryable failures before retries are suppressed.
 *
 * **Counted in retry decisions, not requests.** axios-retry short-circuits
 * `retryCount < retries && retryCondition(error)`, so a fully-failing idempotent request consults
 * this twice rather than three times, and a request that is never retried at all (a `POST`)
 * consults it once. Five therefore lands at roughly three failing requests.
 *
 * Five is the conservative end on purpose: patchy cell service is ordinary for this audience, and a
 * couple of requests failing together on a bad signal should still get their full retries. A landing
 * load that fails outright contributes four — two content reads at two decisions each, after which
 * the app stops calling — so a blip on page load clamps nobody. The fifth comes from the customer's
 * next action, which is the point at which the failures are no longer plausibly theirs.
 * Tightening it later is cheap, and the report below says how often it fires.
 */
const TRIP_AFTER_CONSECUTIVE_FAILURES = 5

/**
 * How long retries stay suppressed after the **latest** failure once tripped. Every further failure
 * restarts it, so retries stay off while the API keeps failing and return on the first response; the
 * minute only lapses on its own when nothing is sent. Re-probing sooner is what caused the problem.
 */
const SUPPRESSION_MS = 60_000

let consecutiveFailures = 0
let suppressedUntil = 0
let reportedForThisTrip = false

/**
 * Any successful response — the API is answering, so whatever we were counting is over. A 4xx or a
 * bare 500 neither counts nor resets: it is usually about the one request, not the API's health.
 */
export function recordApiSuccess(): void {
  consecutiveFailures = 0
  suppressedUntil = 0
  reportedForThisTrip = false
}

/**
 * A failure of the shape a retry would target: the request never arrived, or the server said it
 * could not handle it right now. Counted regardless of method — a failed `POST` is evidence about
 * the API's health even though it will never be retried itself.
 */
export function recordRetryableFailure(): void {
  consecutiveFailures += 1

  if (consecutiveFailures < TRIP_AFTER_CONSECUTIVE_FAILURES) return

  suppressedUntil = Date.now() + SUPPRESSION_MS

  if (reportedForThisTrip) return
  reportedForThisTrip = true

  appErrorService.reportError(new Error('Retries suppressed after consecutive API failures'), {
    context: 'apiRetrySuppression',
    consecutiveFailures,
    suppressionMs: SUPPRESSION_MS
  })
}

/** Whether a retry may go out right now. */
export function retriesAllowed(): boolean {
  return Date.now() >= suppressedUntil
}

/** Test seam — the module holds process-wide state that a test needs to clear between cases. */
export function resetApiRetrySuppression(): void {
  consecutiveFailures = 0
  suppressedUntil = 0
  reportedForThisTrip = false
}
